import React, { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { ChevronDown } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { addNewProjectStatus } from '../../api/statuses';

interface DropdownCell {
  row: any;
  debouncedSearch: string;
  projectStatuses: { value: string; name: string }[];
  handleUpdateProject: (row: any, column: string, value: string) => void;
  highlightText: (text: any, searchTerm: any, delay?: number) => any;
  handleStatusColor: (key: any, val: any) => any;
  handleRefetchProjectStatuses: () => any;
  disabled?: boolean;
}

export const DropdownCell = ({
  row,
  debouncedSearch,
  projectStatuses,
  handleUpdateProject,
  highlightText,
  handleStatusColor,
  handleRefetchProjectStatuses,
  disabled = false,
}: DropdownCell) => {
  const [newStatus, setNewStatus] = useState(false);
  const [newStatusValue, setNewStatusValue] = useState('');
  const [open, setOpen] = useState<boolean>(false)

  const handleSetNewStatus = () => {
    if (disabled) return;
    setNewStatus(prev => !prev)
    setNewStatusValue('')
  };
  const handleChangeNewStatus = (e: any) => {
    if (disabled) return;
    setNewStatusValue(e.target.value);
  };

  const handleSaveNewStatus = async () => {
    if (disabled) return;
    let accountId = Number(localStorage.getItem('ownerAccountId')) || Number(localStorage.getItem('accountId'));
    if (newStatusValue && newStatusValue.trim()) {
      const payload: { accountId?: number; name: string } = {
        name: newStatusValue,
      };
      if (accountId) {
        payload['accountId'] = accountId;
      }
      const res = await addNewProjectStatus(payload);
      if (res && !res.error) {
        setNewStatusValue('');
        setNewStatus(false);

        handleRefetchProjectStatuses();
      } else {
        console.error(res.error, res.message);
      }
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(val) => {
        if (disabled) return;
        setOpen(val)
        if (!val) {
          setNewStatusValue('')
          setNewStatus(false)
        }
      }}
    >
      <PopoverTrigger data-qa={`projects_row_${row?.original?.id}_project_status_trigger`} asChild>
        <div
          className={`inline-flex items-center gap-1 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${handleStatusColor('status', row?.original?.status)}`}
        >
          <span>{highlightText(row?.original?.status || 'New', debouncedSearch)}</span>
          <ChevronDown size={16} />
        </div>
      </PopoverTrigger>

      <PopoverContent data-qa={`projects_row_${row?.original?.id}_project_status_content`} align="start" className="z-[501] min-w-[8rem] overflow-hidden rounded-md p-1 text-popover-foreground data-[state=open]:animate-animate-in data-[state=closed]:animate-animate-out data-[side=bottom]:animate-slide-in-from-top-2 data-[side=left]:animate-slide-in-from-right-2 data-[side=right]:animate-slide-in-from-left-2 data-[side=top]:animate-slide-in-from-bottom-2 w-48 bg-background border border-input shadow-md">
        <div className="border-b border-input mb-1 pb-1">
          <li
            data-qa={`projects_row_${row?.original?.id}_project_status_create_new_status_trigger`}
            onClick={handleSetNewStatus}
            className="relative hover:bg-accent flex gap-1 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-primary cursor-pointer"
          >
            <span>+</span> New Status
          </li>

          {newStatus && (
            <div className="max-w-[200px] flex items-center gap-2 px-2 py-1.5">
              <Input
                data-qa={`projects_row_${row?.original?.id}_project_status_create_status_input`}
                value={newStatusValue}
                onChange={handleChangeNewStatus}
                disabled={disabled}
                placeholder="Enter Name..."
                className="min-h-[0px] outline-none flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-7 text-xs flex-1"
              />
              {newStatusValue && (
                <Button
                  data-qa={`projects_row_${row?.original?.id}_project_status_create_status_trigger`}
                  onClick={handleSaveNewStatus}
                  disabled={disabled}
                  className="text-primary p-0 m-0 min-h-[max-content] h-[max-content]"
                  variant={'text'}
                  type="button"
                >
                  Save
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="mt-1 flex flex-col gap-1 max-h-[400px] overflow-y-auto overflow-x-hidden">
          {projectStatuses.map((s) => (
            <div
              data-qa={`projects_row_${row?.original?.id}_project_status_item_${s?.value}`}
              key={s.value}
              onClick={() => {
                if (disabled) return;
                handleUpdateProject(row.original, 'status', s.value);
              }}
              className={`relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-gray-500 ${handleStatusColor('status', s?.value)}`}
            >
              {s.name}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
